import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import ButtonBase from "@mui/material/ButtonBase";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { TreeItem } from "./types";
import { filterTreeViewItems } from "./utils";
import { useExpandTree } from "./hooks/useExpandTree";

interface TreeViewListProps {
  filter: string;
  items: TreeItem[];
  onItemSelect?: (item: TreeItem) => void;
}

export const TreeViewList = ({
  filter,
  items,
  onItemSelect,
}: TreeViewListProps) => {
  const normalizedFilter = filter.trim().toLowerCase();

  const { expanded, toggle, ready } = useExpandTree(items);

  const filteredItems = items
    .map((item) => filterTreeViewItems(normalizedFilter, item))
    .filter((item) => item !== null);

  const handleAccordionChange =
    (itemId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      toggle(itemId, isExpanded);
    };

  const handleItemClick = (item: TreeItem) => () => {
    onItemSelect?.(item);
  };

  const renderTreeItems = (treeItems: TreeItem[], level = 0) => {
    return treeItems.map((item) => {
      const itemId = item.id.toString();
      const hasChildren = item.children && item.children.length > 0;

      if (hasChildren) {
        const isItemExpanded = expanded.includes(itemId);
        return (
          <Accordion
            key={itemId}
            expanded={ready === false || isItemExpanded}
            onChange={() => {}} // Disable default accordion behavior
            disableGutters
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              backgroundColor: "transparent",
              width: "100%",
              "& .MuiCollapse-root": {
                transition:
                  "height 150ms cubic-bezier(0.4, 0, 0.2, 1) !important",
              },
            }}
          >
            <AccordionSummary
              expandIcon={null}
              onClick={() => onItemSelect?.(item)}
              sx={{
                "& .MuiAccordionSummary-content": {
                  margin: "0",
                  alignItems: "center",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "0",
                },
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  borderRadius: 1,
                },
                "&:active": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}
              component={ButtonBase}
              TouchRippleProps={{
                center: false,
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <Box
                  display="flex"
                  component="span"
                  paddingLeft={`${level * 12}px`}
                  alignItems="center"
                  mr="12px"
                  width="20px"
                  justifyContent="center"
                  sx={{
                    cursor: "pointer",
                    transition: "transform 0.15s ease",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccordionChange(itemId)(e, !isItemExpanded);
                  }}
                >
                  <KeyboardArrowRightIcon
                    fontSize="small"
                    sx={{
                      transform:
                        ready === false || isItemExpanded
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </Box>
                <Typography sx={{ flexGrow: 1 }}>{item.label}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <List disablePadding sx={{ width: "100%" }}>
                {renderTreeItems(item.children || [], level + 1)}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      } else {
        return (
          <ListItem
            key={itemId}
            sx={{
              py: 0,
              paddingRight: 0,
              width: "100%",
            }}
            disablePadding
          >
            <ListItemButton
              onClick={handleItemClick(item)}
              sx={{
                width: "100%",
                height: "48px",
                paddingLeft: `${level * 16 + 40}px`,
                borderRadius: 1,
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        );
      }
    });
  };

  return (
    <List disablePadding sx={{ width: "100%" }}>
      {renderTreeItems(filteredItems)}
    </List>
  );
};
